'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CircleHelp, CircleX } from 'lucide-react';
import { useModalStore } from '@/stores/useModalStore';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import GearScore from '@/components/features/gear/GearScore';
import GearScoreExplanation from '@/(routers)/gears/detail/[gearId]/components/modal/GearScoreExplanation';

interface EDIYScore {
    score_daily: number;
    score_specialized: number;
    score_software: number;
    score_hardware: number;
    score_innovation: number;
    score_versatile: number;
}

interface ScoreResult {
    ediy_score: EDIYScore;
    code_score: number;
    comment: string;
}

interface BoardAIScoreOverlayProps {
    show: boolean;
    isScoring: boolean;
    scoreResult: ScoreResult | null;
    onClose: () => void;
    containerHeight: number;
    containerWidth: number;
    isDark: boolean;
}

const EDIY_KEYS: (keyof EDIYScore)[] = [
    'score_daily',
    'score_specialized',
    'score_software',
    'score_hardware',
    'score_innovation',
    'score_versatile',
];

const EMPTY_SCORE: EDIYScore = {
    score_daily: 1,
    score_specialized: 1,
    score_software: 1,
    score_hardware: 1,
    score_innovation: 1,
    score_versatile: 1,
};

function ScoreRing({ score }: { score: number }) {
    const r = 36;
    const circ = 2 * Math.PI * r;
    const dash = (score / 100) * circ;

    return (
        <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
            <svg width={96} height={96} viewBox="0 0 96 96" className="-rotate-90">
                <circle cx={48} cy={48} r={r} fill="none" stroke="#fef3c7" strokeWidth={8} />
                <motion.circle
                    cx={48}
                    cy={48}
                    r={r}
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth={8}
                    strokeLinecap="round"
                    strokeDasharray={circ}
                    initial={{ strokeDashoffset: circ }}
                    animate={{ strokeDashoffset: circ - dash }}
                    transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }}
                />
            </svg>
            <motion.span
                className="absolute font-cantarell font-black text-2xl text-amber-500 tabular-nums"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', delay: 0.4 }}
            >
                {score}
            </motion.span>
        </div>
    );
}

function PageDots({ count, current, onChange }: { count: number; current: number; onChange: (i: number) => void }) {
    return (
        <div className="flex items-center justify-center gap-2 flex-shrink-0 py-1">
            {Array.from({ length: count }).map((_, i) => (
                <button
                    key={i}
                    onClick={() => onChange(i)}
                    className="rounded-full transition-all duration-300"
                    style={{
                        width: i === current ? 20 : 8,
                        height: 8,
                        background: i === current ? '#f59e0b' : '#fde68a',
                    }}
                />
            ))}
        </div>
    );
}

export default function BoardAIScoreOverlay({
    show,
    isScoring,
    scoreResult,
    onClose,
    containerHeight,
    containerWidth,
    isDark,
}: BoardAIScoreOverlayProps) {
    const { openModal } = useModalStore();
    const [delayedScoreResult, setDelayedScoreResult] = useState<ScoreResult | null>(null);
    const [animatedScore, setAnimatedScore] = useState<EDIYScore>(EMPTY_SCORE);
    const [showCodeScore, setShowCodeScore] = useState(false);
    const [activePage, setActivePage] = useState(0);
    const allScoresSetRef = useRef(false);
    const autoSlideDoneRef = useRef(false);
    const autoSlideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragStartX = useRef<number | null>(null);

    const HEIGHT_THRESHOLD = 450;
    const WIDTH_THRESHOLD = 420;
    const isCompactMode =
        (containerHeight > 0 && containerHeight < HEIGHT_THRESHOLD) ||
        (containerWidth > 0 && containerWidth < WIDTH_THRESHOLD);
    const isSlideMode = isCompactMode;
    const isCompactHeader = isCompactMode;

    const ediyCardStyle = isDark
        ? { background: '#2d2d2d', border: '1.5px solid #374151' }
        : { background: '#ffffff', border: '1.5px solid #e5e7eb' };

    const codeCardStyle = isDark
        ? { background: 'linear-gradient(135deg, #2d2500 0%, #2d1f00 100%)', border: '1.5px solid #92400e' }
        : { background: 'linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%)', border: '1.5px solid #fde68a' };

    const ediyLabelClass = isDark ? 'text-sky-400' : 'text-color-block-ai-label';
    const commentClass = isDark ? 'text-gray-300' : 'text-gray-600';

    useEffect(() => {
        if (!show) {
            setDelayedScoreResult(null);
            setAnimatedScore(EMPTY_SCORE);
            setShowCodeScore(false);
            setActivePage(0);
            allScoresSetRef.current = false;
            autoSlideDoneRef.current = false;
            if (autoSlideTimerRef.current) {
                clearTimeout(autoSlideTimerRef.current);
                autoSlideTimerRef.current = null;
            }
            return;
        }

        setDelayedScoreResult(null);
        setAnimatedScore(EMPTY_SCORE);
        setShowCodeScore(false);
        setActivePage(0);
        allScoresSetRef.current = false;
        autoSlideDoneRef.current = false;

        const initTimer = setTimeout(() => {
            setDelayedScoreResult(scoreResult);
            if (!scoreResult) return;

            EDIY_KEYS.forEach((key, i) => {
                setTimeout(() => {
                    setAnimatedScore((prev) => ({ ...prev, [key]: scoreResult.ediy_score[key] }));
                }, i * 500);
            });

            setTimeout(() => {
                allScoresSetRef.current = true;
            }, EDIY_KEYS.length * 500);
        }, 700);

        return () => {
            clearTimeout(initTimer);
            if (autoSlideTimerRef.current) {
                clearTimeout(autoSlideTimerRef.current);
                autoSlideTimerRef.current = null;
            }
        };
    }, [show, scoreResult]);

    const handleGearAnimationComplete = useCallback(() => {
        if (!allScoresSetRef.current) return;
        setShowCodeScore(true);
        if (isSlideMode && !autoSlideDoneRef.current) {
            autoSlideDoneRef.current = true;
            autoSlideTimerRef.current = setTimeout(() => setActivePage(1), 400);
        }
    }, [isSlideMode]);

    const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
        dragStartX.current = 'touches' in e ? e.touches[0].clientX : e.clientX;
    };
    const handleDragEnd = (e: React.TouchEvent | React.MouseEvent) => {
        if (dragStartX.current === null) return;
        const endX = 'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
        const diff = dragStartX.current - endX;
        if (Math.abs(diff) > 40) {
            if (diff > 0 && activePage < 1) setActivePage(1);
            if (diff < 0 && activePage > 0) setActivePage(0);
        }
        dragStartX.current = null;
    };

    const normalModeContent = (
        <div className="flex-1 flex flex-col px-3 py-2 gap-2 min-h-0 overflow-hidden">
            <div
                className="flex-1 flex flex-col min-h-0 rounded-2xl px-1 py-2 relative overflow-hidden"
                style={ediyCardStyle}
            >
                <ImageAdaptive
                    src="/images/ai/score-02.png"
                    baseWidth={110}
                    className="absolute bottom-0 right-0 object-contain z-10 pointer-events-none"
                />
                <div className="flex w-full items-center flex-shrink-0 px-2">
                    <span className={`flex-grow font-corp text-sm font-bold ${ediyLabelClass}`}>
                        ホワイト先生が算出したeDIYスコア
                    </span>
                    <div
                        className="flex justify-center items-center pl-2 text-color-gear-score-question cursor-pointer"
                        onClick={() =>
                            openModal({
                                id: 'gear-score-explanation',
                                component: <GearScoreExplanation />,
                                size: {
                                    pcWidth: '800px',
                                    mbWidth: '90%',
                                    pcHeight: '650px',
                                    mbHeight: '80%',
                                },
                                direction: 'clip',
                                overFlowType: 'auto',
                            })
                        }
                    >
                        <IconAdaptive Icon={CircleHelp} strokeWidth={2.0} baseSize={22} />
                    </div>
                </div>
                <div className="flex justify-center items-center w-full flex-1 min-h-0">
                    <div style={{ width: '100%', maxWidth: 310, aspectRatio: '1' }}>
                        <GearScore
                            scores={animatedScore}
                            showScores={true}
                            onAnimationComplete={handleGearAnimationComplete}
                            isDark={isDark}
                        />
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {showCodeScore && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="rounded-2xl px-4 py-4 flex items-center gap-3 flex-shrink-0"
                        style={codeCardStyle}
                    >
                        <ImageAdaptive
                            src="/images/ai/score-01.png"
                            baseWidth={56}
                            className="flex-shrink-0 object-contain self-end"
                        />
                        <ScoreRing score={delayedScoreResult!.code_score} />
                        <div className="flex-1 min-w-0">
                            <p className="font-corp text-sm font-bold text-amber-500 mb-0.5">コード評価</p>
                            <p className={`font-corp text-[13px] font-medium leading-snug ${commentClass}`}>
                                {delayedScoreResult!.comment}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );

    if (!show) return null;

    return (
        <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="absolute inset-0 z-30 flex flex-col"
            style={{ background: isDark ? '#1a1a1a' : '#fafaf8' }}
        >
            {/* ヘッダー */}
            <div
                className="flex items-center justify-between flex-shrink-0"
                style={{
                    padding: isCompactHeader ? '6px 12px' : '12px 16px',
                    background: 'linear-gradient(90deg, #fff7c2 0%, #ffb30f 100%)',
                    borderBottom: '1.5px solid #ffd700',
                }}
            >
                <div className="flex items-center gap-2">
                    <ImageAdaptive
                        src="/images/ai/score-badge.svg"
                        baseWidth={isCompactHeader ? 24 : 36}
                        className="flex-shrink-0"
                    />
                    <span
                        className="font-corp font-bold text-amber-600"
                        style={{ fontSize: isCompactHeader ? 13 : 15 }}
                    >
                        ホワイト先生のプログラミング評価
                    </span>
                </div>
                <button onClick={onClose} className="flex items-center justify-center transition-colors">
                    <IconAdaptive
                        Icon={CircleX}
                        strokeWidth={1.5}
                        baseSize={isCompactHeader ? 24 : 32}
                        className="text-color-white-base"
                    />
                </button>
            </div>

            {/* ローディング */}
            {isScoring && (
                <div className="flex flex-col items-center justify-center flex-1 gap-4">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="w-12 h-12 rounded-full border-4 border-amber-100 border-t-amber-400"
                    />
                    <p className="font-corp text-[14px] font-bold text-amber-400">評価中・・・</p>
                </div>
            )}

            {/* 結果 */}
            {!isScoring && delayedScoreResult && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-1 flex flex-col min-h-0"
                >
                    {isSlideMode ? (
                        <>
                            <div
                                className="flex-1 relative overflow-hidden min-h-0"
                                onTouchStart={handleDragStart}
                                onTouchEnd={handleDragEnd}
                                onMouseDown={handleDragStart}
                                onMouseUp={handleDragEnd}
                                onMouseLeave={() => {
                                    dragStartX.current = null;
                                }}
                            >
                                {/* eDIYスコアページ */}
                                <motion.div
                                    className="absolute inset-0 flex flex-col px-3 py-2"
                                    animate={{ x: activePage === 0 ? '0%' : '-100%' }}
                                    transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
                                >
                                    <div
                                        className="flex-1 flex flex-col min-h-0 rounded-2xl px-1 py-2 relative overflow-clip"
                                        style={ediyCardStyle}
                                    >
                                        <ImageAdaptive
                                            src="/images/ai/score-02.png"
                                            baseWidth={80}
                                            className="absolute bottom-0 right-0 object-contain z-10 pointer-events-none"
                                        />
                                        <div className="flex w-full items-center flex-shrink-0 px-2">
                                            <span
                                                className={`flex-grow font-corp text-xs font-bold min-w-0 ${ediyLabelClass}`}
                                            >
                                                ホワイト先生が算出したeDIYスコア
                                            </span>
                                            <div
                                                className="flex justify-center items-center pl-2 text-color-gear-score-question cursor-pointer"
                                                onClick={() =>
                                                    openModal({
                                                        id: 'gear-score-explanation',
                                                        component: <GearScoreExplanation />,
                                                        size: {
                                                            pcWidth: '800px',
                                                            mbWidth: '90%',
                                                            pcHeight: '650px',
                                                            mbHeight: '80%',
                                                        },
                                                        direction: 'clip',
                                                        overFlowType: 'auto',
                                                    })
                                                }
                                            >
                                                <IconAdaptive Icon={CircleHelp} strokeWidth={2.0} baseSize={22} />
                                            </div>
                                        </div>
                                        <div className="flex justify-center items-center w-full flex-1 min-h-0">
                                            <div style={{ width: '100%', maxWidth: 250, aspectRatio: '1' }}>
                                                <GearScore
                                                    scores={animatedScore}
                                                    showScores={true}
                                                    onAnimationComplete={handleGearAnimationComplete}
                                                    isDark={isDark}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* コード評価ページ */}
                                <motion.div
                                    className="absolute inset-0 flex flex-col justify-center px-3 py-2 overflow-hidden"
                                    initial={false}
                                    animate={{ x: activePage === 1 ? '0%' : '100%' }}
                                    transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
                                >
                                    <AnimatePresence>
                                        {showCodeScore && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.4 }}
                                                className="rounded-2xl px-4 py-4 flex flex-col gap-3"
                                                style={codeCardStyle}
                                            >
                                                <div className="flex items-end justify-center gap-3 flex-shrink-0">
                                                    <ImageAdaptive
                                                        src="/images/ai/score-01.png"
                                                        baseWidth={70}
                                                        className="flex-shrink-0 object-contain"
                                                    />
                                                    <ScoreRing score={delayedScoreResult.code_score} />
                                                </div>
                                                <div>
                                                    <p className="font-corp text-sm font-bold text-amber-500 mb-0.5">
                                                        コード評価
                                                    </p>
                                                    <p
                                                        className={`font-corp text-[13px] font-medium leading-snug ${commentClass}`}
                                                    >
                                                        {delayedScoreResult.comment}
                                                    </p>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            </div>

                            <PageDots
                                count={2}
                                current={activePage}
                                onChange={(i) => {
                                    if (i === 1 && !showCodeScore) return;
                                    setActivePage(i);
                                }}
                            />
                        </>
                    ) : (
                        normalModeContent
                    )}
                </motion.div>
            )}
        </motion.div>
    );
}
